import { Button, ColorTypes, actions } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { ColumnSource, RowSource, CellModel, RequestFilters } from "@omegagrid/grid";
import { ToolbarItem, ToolbarDropdown } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, formatDateTime, DEFAULT_GRID_EDITOR_OPTIONS } from "@reporting/rp-app-host";
import { StatementDefinition, StatementRecoveryChange, StatementRecoveryHistoryItem } from "@reporting/rp-types/definition";
import { NodeSource, TreeSource } from "@omegagrid/tree";

const statusColorMap: Record<string, string> = {
	'CREATED': '#0c860c',
	'DELETED': '#c20808',
	'ARCHIVED': '#0373c9',
}

export class ReportRecoveryGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	private _idStatement: number = null;
	private set idStatement(value: number) {
		this._idStatement = value;
		this._date = null;
		this.hasChanges = false;
		this.editor.refresh();
	}

	private _date: string = null;
	private set date(value: string) {
		this._date = value;
		this.hasChanges = false;
		this.editor.refresh();
	}

	private set hasChanges(value: boolean) {
		const btn = this.editor?.customToolbar?.controls?.get('btnRecover') as Button;
		if (btn) btn.disabled = !value;
	}	

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'idz',
			header: '#',
			t: 'n',
			z: '0',
			w: 100,
		}, {
			data: 'table',
			header: msg('Table'),
			t: 't',
			w: 220,
		}, {
			data: 'description',
			header: msg('Description'),
			t: 't',
			w: 450,
		}, {
			data: 'status',
			header: msg('Status'),
			t: 's',
			content: [
				{ k: 'CREATED', v: msg('Created') },
				{ k: 'DELETED', v: msg('Deleted') },
				{ k: 'ARCHIVED', v: msg('Archived') },
			],
			w: 100,
			s: { alignment: { horizontal: 'center' } }
		}, {
			data: 'date',
			header: msg('Date'),
			t: 'dt',
			e: false,
			w: 145,
		}, {
			data: 'who',
			header: msg('User'),
			t: 't',
			e: false,
			w: 145,
		}];
		return this._columns;
	}

	private buildHistoryTree(history: StatementRecoveryHistoryItem[]) {
		const items: TreeSource = [];
		let lastYear: number = null;
		let lastMonth: number = null;

		for (const h of history) {
			const dt = new Date(h.recovery_date);
			const year = dt.getFullYear();
			const month = dt.getMonth() + 1;

			if (year !== lastYear) {
				items.push({ v: String(year), l: 0, ch: false, collapsed: true });
				lastYear = year;
				lastMonth = null;
			}
			if (month !== lastMonth) {
				items.push({ v: msg(`m${month}`), l: 1, ch: false, collapsed: true });
				lastMonth = month;
			}
			items.push({
				k: h.recovery_date,
				v: `${formatDateTime(h.recovery_date)} (${h.login_name || h.user_name || '?'} +${h.change_count})`,
				l: 2,
			});
		}
		return items;
	}

	private async loadHistory() {
		if (!this._idStatement) return;
		const ddDate = this.editor.customToolbar.controls.get('ddDate') as ToolbarDropdown;
		ddDate.tree.value = null;
		ddDate.tree.setSourceData([{ k: null, v: `${msg('Loading')}...` }]);

		const history = await rp.ms('definition').get<StatementRecoveryHistoryItem[]>(`statements/recovery/${this._idStatement}`);

		const items = this.buildHistoryTree(history);
		ddDate.tree.setSourceData(items);
		this.editor.customToolbar.items.find(item => item.id === 'ddDate').items = items;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		if (!this._idStatement || !this._date || this._date == 'null') return [];
		const changes = await rp.ms('definition').patch<StatementRecoveryChange[]>('statements/recovery', {
			id_statement: this._idStatement,
			date: this._date,
			dryRun: true,
		});
		this.hasChanges = changes.length > 0;
		return changes.map(data => ({ data: {
			...data,
			who: data.who_login_name || data.who || '?',
			status: { t: 't', v: data.status, dv: msg(data.status.toLocaleLowerCase()) },
		}}));
	}

	private async recover() {
		if (!this._idStatement || !this._date || this._date == 'null') return;
		this.editor.showLoader();
		await rp.ms('definition').patch('statements/recovery', {
			id_statement: this._idStatement,
			date: this._date,
			dryRun: false,
		});
		this.editor.hideLoader();
		rp.host.bucket.showAlert({ content: msg('Completed successfully.'), type: 'success', title: msg('Report recovery') });
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		const statements = await rp.ms('definition').get<StatementDefinition[]>('statements/recovery');

		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			editable: false,
			pageSize: null,
			toolbar: {
				refresh: { enabled: false },
				new: { enabled: false },
				save: { enabled: false },
				discard: { enabled: false },
			},
			customToolbar: [{
				type: 'dropdown',
				id: 'ddStatement',
				caption: msg('Report'),
				width: 300,
				items: statements.map(s => ({
					k: String(s.id_statement),
					v: `#${s.id_statement} ${s.statement}`,
					icon: s.deleted ? { icon: 'trash', color: '#c20808' } : 'file',
				})),
				options: {
					search: true,
					autoClose: true,
				},
			}, {
				type: 'dropdown',
				id: 'ddDate',
				caption: msg('Date'),
				width: 220,
				items: [],
				options: {
					autoClose: true,
					expanders: true,
					globalLevels: true
				},
			}, {
				type: 'button',
				id: 'btnRecover',
				disabled: true,
				text: msg('Recover'),
				icon: 'clock-rotate-left',
				color: ColorTypes.Green
			}] as ToolbarItem[],
			afterRenderer: (div: HTMLDivElement, cell: CellModel) => {
				if (cell.column.data === 'status') {
					div.style.color =  statusColorMap[cell.value as string];
				}
				return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
			}
		});
	}

	async onAction(action: string, cell: CellModel, event: actions.ActionEvent) {
		switch (action) {
			case 'ddStatement':
				this.idStatement = parseInt(String(event.action.value));
				await this.loadHistory();
				break;
			case 'ddDate':
				this.date = String(event.action.value);
				break;
			case 'btnRecover':
				await this.recover();
				break;
		}
	}

	// workaround to set dynamicWidth for dropdowns... TODO: add dynamicWidth to ToolbarItem?
	afterLoad() {
		const ddStatement = this.editor.customToolbar.controls.get('ddStatement') as ToolbarDropdown;
		const ddDate = this.editor.customToolbar.controls.get('ddDate') as ToolbarDropdown;	
		ddDate.tree.dynamicWidth = ddStatement.tree.dynamicWidth = true;
	}

}
